import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  CloudDownload as ImportIcon,
} from '@mui/icons-material';
import S3BucketForm from '../../components/S3BucketForm';
import S3BucketImportDialog from '../../components/S3BucketImportDialog';
import {
  getS3Buckets,
  deleteS3Bucket,
  getBucketFolders,
} from '../../api/s3';

function S3Buckets() {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [expandedBucket, setExpandedBucket] = useState(null);
  const [bucketFolders, setBucketFolders] = useState({});
  const [loadingFolders, setLoadingFolders] = useState({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchBuckets();
  }, []);

  const fetchBuckets = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getS3Buckets();
      setBuckets(data);
    } catch (error) {
      console.error('Failed to fetch S3 buckets:', error);
      setError('Failed to load buckets');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async (bucketId) => {
    try {
      setLoadingFolders(prev => ({ ...prev, [bucketId]: true }));
      const data = await getBucketFolders(bucketId);
      setBucketFolders(prev => ({ ...prev, [bucketId]: data.folders }));
    } catch (error) {
      console.error('Failed to fetch bucket folders:', error);
    } finally {
      setLoadingFolders(prev => ({ ...prev, [bucketId]: false }));
    }
  };

  const handleExpandBucket = async (bucketId) => {
    if (expandedBucket === bucketId) {
      setExpandedBucket(null);
    } else {
      setExpandedBucket(bucketId);
      if (!bucketFolders[bucketId]) {
        await fetchFolders(bucketId);
      }
    }
  };

  const handleCreate = () => {
    setSelectedBucket(null);
    setDialogOpen(true);
  };

  const handleEdit = (bucket) => {
    setSelectedBucket(bucket);
    setDialogOpen(true);
  };

  const handleDelete = async (bucketId, bucketName) => {
    if (window.confirm(`Are you sure you want to delete the bucket configuration "${bucketName}"? This will NOT delete the actual S3 bucket.`)) {
      try {
        await deleteS3Bucket(bucketId);
        fetchBuckets();
      } catch (error) {
        console.error('Failed to delete bucket:', error);
        setError('Failed to delete bucket configuration');
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedBucket(null);
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    setSelectedBucket(null);
    fetchBuckets();
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    fetchBuckets();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">S3 Buckets</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchBuckets}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportDialogOpen(true)}
            sx={{ mr: 2 }}
          >
            Import Existing
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Add Bucket
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>Bucket Name</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Role ARN</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : buckets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary" py={4}>
                    No S3 buckets configured. Click "Add Bucket" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              buckets.map((bucket) => (
                <React.Fragment key={bucket.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleExpandBucket(bucket.id)}
                      >
                        {expandedBucket === bucket.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                        {bucket.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bucket.region}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {bucket.aws_role_arn ? `...${bucket.aws_role_arn.slice(-20)}` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {bucket.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(bucket.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(bucket)}
                        title="Edit Bucket"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(bucket.id, bucket.name)}
                        title="Delete Bucket"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedBucket === bucket.id ? undefined : 'none' }}>
                      <Collapse in={expandedBucket === bucket.id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, pl: 6 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Content Folders
                          </Typography>
                          {loadingFolders[bucket.id] ? (
                            <CircularProgress size={20} />
                          ) : bucketFolders[bucket.id]?.length > 0 ? (
                            <List dense>
                              {bucketFolders[bucket.id].map((folder) => (
                                <ListItem key={folder}>
                                  <ListItemIcon>
                                    <FolderIcon color="primary" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={folder}
                                    primaryTypographyProps={{ fontFamily: 'monospace' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No content folders found. Upload HLS/DASH content to create folders.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <S3BucketForm
          bucket={selectedBucket}
          onClose={handleDialogClose}
          onSuccess={handleSuccess}
        />
      </Dialog>

      <S3BucketImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </Box>
  );
}

export default S3Buckets;
